import { useTranslation } from 'react-i18next';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import { useSEO, SITE_URL } from '@/hooks/useSEO';

export default function ElectricalPage() {
  const { t } = useTranslation();

  useSEO({
    title: 'Elektrik Hizmetleri | Canel Elektrik – Orta Gerilim, Otomasyon, Zayıf Akım',
    description:
      "Orta gerilim sistemleri, alçak gerilim tesisatı, zayıf akım güvenlik sistemleri ve endüstriyel otomasyon alanında anahtar teslim elektrik mühendisliği hizmetleri. İstanbul merkezli Türkiye geneli uygulama.",
    keywords:
      'orta gerilim sistemi, elektrik mühendisliği İstanbul, PLC SCADA otomasyon, yangın algılama sistemi, endüstriyel elektrik taahhüt',
    canonical: '/electrical',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Elektrik Mühendisliği Hizmetleri – Canel Elektrik',
      url: `${SITE_URL}/electrical`,
      description:
        'Orta gerilimden otomasyon sistemlerine kadar eksiksiz endüstriyel elektrik mühendisliği çözümleri.',
      provider: {
        '@type': 'Organization',
        name: 'Canel Elektrik İnşaat Taahhüt Mühendislik',
        url: SITE_URL,
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Perpa Ticaret Merkezi, A Blok Kat: 5 No: 204',
          addressLocality: 'Şişli',
          addressRegion: 'İstanbul',
          addressCountry: 'TR',
        },
      },
      serviceType: 'Elektrik Taahhüt Mühendisliği',
      areaServed: {
        '@type': 'Country',
        name: 'Türkiye',
      },
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Elektrik Hizmetleri',
        itemListElement: [
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Orta Gerilim Sistemleri' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Alçak Gerilim Tesisatı' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Zayıf Akım & Güvenlik' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'PLC/SCADA Otomasyon' } },
        ],
      },
    },
  });

  const services = [
    {
      icon: 'ri-flashlight-line',
      titleKey: 'svc_mv_title',
      descKey: 'svc_mv_desc',
      detailKeys: ['elec_detail_mv1', 'elec_detail_mv2', 'elec_detail_mv3', 'elec_detail_mv4', 'elec_detail_mv5', 'elec_detail_mv6'],
      image: 'https://readdy.ai/api/search-image?query=high%20voltage%20electrical%20substation%20transformer%20power%20distribution%20industrial%20Turkey%20professional%20engineering&width=600&height=380&seq=elec1&orientation=landscape',
    },
    {
      icon: 'ri-lightbulb-line',
      titleKey: 'svc_lv_title',
      descKey: 'svc_lv_desc',
      detailKeys: ['elec_detail_lv1', 'elec_detail_lv2', 'elec_detail_lv3', 'elec_detail_lv4', 'elec_detail_lv5', 'elec_detail_lv6'],
      image: 'https://readdy.ai/api/search-image?query=low%20voltage%20electrical%20panel%20installation%20industrial%20facility%20interior%20professional%20engineering%20Turkey&width=600&height=380&seq=elec2&orientation=landscape',
    },
    {
      icon: 'ri-shield-check-line',
      titleKey: 'svc_weak_title',
      descKey: 'svc_weak_desc',
      detailKeys: ['elec_detail_weak1', 'elec_detail_weak2', 'elec_detail_weak3', 'elec_detail_weak4', 'elec_detail_weak5', 'elec_detail_weak6'],
      image: 'https://readdy.ai/api/search-image?query=fire%20detection%20security%20CCTV%20access%20control%20system%20installation%20industrial%20building%20Turkey%20professional&width=600&height=380&seq=elec3&orientation=landscape',
    },
    {
      icon: 'ri-cpu-line',
      titleKey: 'svc_auto_title',
      descKey: 'svc_auto_desc',
      detailKeys: ['elec_detail_auto1', 'elec_detail_auto2', 'elec_detail_auto3', 'elec_detail_auto4', 'elec_detail_auto5', 'elec_detail_auto6'],
      image: 'https://readdy.ai/api/search-image?query=PLC%20SCADA%20automation%20control%20room%20industrial%20facility%20Turkey%20professional%20engineering%20technology&width=600&height=380&seq=elec4&orientation=landscape',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative h-72 md:h-80 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=electrical%20engineering%20high%20voltage%20power%20lines%20substation%20industrial%20Turkey%20dramatic%20sky%20professional&width=1920&height=500&seq=elec-hero&orientation=landscape"
          alt="Electrical Services"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1A2332]/80 to-[#1A2332]/40" />
        <div className="absolute inset-0 flex flex-col justify-center px-6 md:px-16 max-w-7xl mx-auto w-full">
          <span className="text-[#E8A020] text-xs font-bold tracking-widest uppercase mb-3">{t('services_electrical_title')}</span>
          <h1 className="text-3xl md:text-5xl font-black text-white">{t('services_electrical_title')}</h1>
          <p className="text-white/70 mt-3 text-base max-w-xl">{t('elec_hero_subtitle')}</p>
        </div>
      </section>

      {/* Services */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 flex flex-col gap-20">
          {services.map((svc, idx) => (
            <div key={svc.titleKey} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${idx % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
              <div className={idx % 2 === 1 ? 'lg:order-2' : ''}>
                <div className="w-12 h-12 flex items-center justify-center bg-[#E8A020]/10 rounded-lg mb-5">
                  <i className={`${svc.icon} text-[#E8A020] text-2xl`} />
                </div>
                <h2 className="text-2xl font-black text-[#1A2332] mb-4">{t(svc.titleKey)}</h2>
                <p className="text-gray-500 text-base leading-relaxed mb-6">{t(svc.descKey)}</p>
                <ul className="grid grid-cols-2 gap-2">
                  {svc.detailKeys.map((dk) => (
                    <li key={dk} className="flex items-center gap-2 text-sm text-gray-600">
                      <i className="ri-check-line text-[#E8A020]" /> {t(dk)}
                    </li>
                  ))}
                </ul>
              </div>
              <div className={`w-full h-72 rounded-lg overflow-hidden ${idx % 2 === 1 ? 'lg:order-1' : ''}`}>
                <img src={svc.image} alt={t(svc.titleKey)} className="w-full h-full object-cover object-top" />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#1A2332]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-black text-white mb-4">{t('cta_title')}</h2>
          <p className="text-white/60 mb-8">{t('cta_desc')}</p>
          <a href="/contact" className="bg-[#E8A020] text-white font-bold px-8 py-4 rounded-full hover:bg-[#d4911a] transition-colors cursor-pointer whitespace-nowrap inline-block">
            {t('nav_quote')}
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
